import { useEffect, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ClientCreateDialog } from '@/components/ClientCreateDialog'
import { fetchClientsPage } from '@/lib/clientsApi'
import type { CalendarClient } from '@/types'

type ClientPickerProps = {
  orgSlug: string
  value: CalendarClient | null
  onChange: (client: CalendarClient | null) => void
  disabled?: boolean
  error?: string
}

function nameFromQuery(query: string) {
  const trimmed = query.trim()
  if (!trimmed || /^\+?\d[\d\s-]*$/.test(trimmed)) return ''
  return trimmed
}

export function ClientPicker({ orgSlug, value, onChange, disabled, error }: ClientPickerProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [results, setResults] = useState<CalendarClient[]>([])
  const [loading, setLoading] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (disabled) return
    const handle = window.setTimeout(async () => {
      setLoading(true)
      try {
        const page = await fetchClientsPage(orgSlug, { page: 1, per: 20, q: query })
        setResults(page.clients.map((client) => ({ id: client.id, name: client.name, phone: client.phone })))
      } catch {
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 250)
    return () => window.clearTimeout(handle)
  }, [orgSlug, query, disabled])

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (creating) return
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [creating])

  const openCreate = () => {
    setOpen(false)
    setCreating(true)
  }

  if (value) {
    return (
      <div className="space-y-2">
        <Label>Cliente</Label>
        <div className="flex h-10 items-center justify-between rounded-md border border-input bg-background px-3 text-sm">
          <span className="truncate">{value.name}</span>
          {!disabled && (
            <button
              type="button"
              className="ml-2 text-muted-foreground hover:text-foreground"
              onClick={() => {
                onChange(null)
                setQuery('')
                setOpen(true)
              }}
            >
              Cambiar
            </button>
          )}
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    )
  }

  return (
    <div className="space-y-2" ref={wrapRef}>
      <Label htmlFor="client-search">Cliente</Label>
      <Input
        id="client-search"
        value={query}
        placeholder="Buscar cliente..."
        disabled={disabled}
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value)
          setOpen(true)
        }}
      />
      {open && !disabled && (
        <div className="overflow-hidden rounded-md border border-border bg-card shadow-sm">
          <div className="max-h-48 overflow-y-auto">
            {loading && <p className="px-3 py-2 text-sm text-muted-foreground">Buscando...</p>}
            {!loading && results.length === 0 && (
              <p className="px-3 py-2 text-sm text-muted-foreground">No se encontraron clientes</p>
            )}
            {results.map((client) => (
              <button
                key={client.id}
                type="button"
                className="flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-accent"
                onClick={() => {
                  onChange(client)
                  setOpen(false)
                }}
              >
                <span className="font-medium">{client.name}</span>
                <span className="text-xs text-muted-foreground">{client.phone}</span>
              </button>
            ))}
          </div>
          <button
            type="button"
            className="w-full border-t border-border px-3 py-2 text-left text-sm font-medium text-primary hover:bg-accent"
            onClick={openCreate}
          >
            + Agregar cliente
          </button>
        </div>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
      {creating && (
        <ClientCreateDialog
          orgSlug={orgSlug}
          initialName={nameFromQuery(query)}
          onClose={() => setCreating(false)}
          onCreated={(client) => {
            onChange(client)
            setCreating(false)
            setQuery('')
          }}
        />
      )}
    </div>
  )
}
